import {
  AxiosError,
  type GenerationNotificationDiagnostic,
  type GenerationNotificationStatus,
  getGenerationNotificationDiagnostics,
  queryKeys,
  reconcileLegacyGenerationNotificationForOwner,
  recoverGenerationNotificationForOwner,
  useAuthStore,
} from "@dutyon/apis";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  legacyReceiptAvailable,
  legacyReceiptMessage,
  notificationApps,
  notificationError,
  notificationKinds,
  notificationReason,
  notificationStates,
  notificationTime,
  recoveryMessage,
} from "./model";

type Session = { owner: string; token: string; nonce: string };
export function GenerationNotificationsPage() {
  const owner = useAuthStore((s) => s.user?.sub),
    token = useAuthStore((s) => s.token),
    role = useAuthStore((s) => s.user?.role);
  const identity = useMemo(
    () => ({ owner, token, role, nonce: crypto.randomUUID() }),
    [owner, token, role],
  );
  if (!owner || !token || role !== "SUPER_ADMIN")
    return <Alert color="red">최고 관리자만 생성 알림을 확인할 수 있습니다.</Alert>;
  return (
    <NotificationOperations
      key={identity.nonce}
      session={{ owner, token, nonce: identity.nonce }}
    />
  );
}
function NotificationOperations({ session }: { session: Session }) {
  const [status, setStatus] = useState<GenerationNotificationStatus>("UNKNOWN"),
    [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [selected, setSelected] = useState<GenerationNotificationDiagnostic | null>(null),
    [legacySelection, setLegacySelection] = useState(false),
    [candidate, setCandidate] = useState(""),
    [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false),
    [needsRefresh, setNeedsRefresh] = useState(false),
    [message, setMessage] = useState<{ text: string; color: string; operationId?: string } | null>(
      null,
    );
  const active = useRef<AbortController | null>(null),
    mounted = useRef(true);
  const candidateInput = useRef<HTMLInputElement>(null);
  const confirmationInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selected) (legacySelection ? confirmationInput.current : candidateInput.current)?.focus();
  }, [selected, legacySelection]);
  const current = () => {
    const state = useAuthStore.getState();
    return (
      mounted.current &&
      state.user?.sub === session.owner &&
      state.token === session.token &&
      state.user.role === "SUPER_ADMIN"
    );
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      active.current?.abort();
    };
  }, []);
  const cursor = cursors[cursors.length - 1];
  const query = useQuery({
    queryKey: queryKeys.generationNotifications.page(session.nonce, status, cursor),
    queryFn: ({ signal }) => {
      if (!current()) throw new Error("계정이 변경되었습니다.");
      return getGenerationNotificationDiagnostics(
        session.owner,
        { status, ...(cursor ? { cursor } : {}) },
        signal,
      );
    },
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const clearSelection = () => {
    setSelected(null);
    setLegacySelection(false);
    setCandidate("");
    setConfirmed(false);
  };
  const ready =
    current() && !busy && !needsRefresh && !query.isFetching && !query.isError && !!query.data;
  async function refresh() {
    if (active.current || !current()) return;
    clearSelection();
    const response = await query.refetch();
    if (current() && !response.isError) setNeedsRefresh(false);
  }
  async function recover() {
    if (
      !ready ||
      !selected ||
      !confirmed ||
      active.current ||
      (legacySelection
        ? !legacyReceiptAvailable(selected)
        : !selected.candidateLookupAvailable || !/^\d{10,16}\.\d{6}$/.test(candidate))
    )
      return;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setMessage(null);
    try {
      if (legacySelection) {
        const result = await reconcileLegacyGenerationNotificationForOwner(
          session.owner,
          selected.id,
          controller.signal,
        );
        if (current())
          setMessage({
            text: legacyReceiptMessage(result),
            color: result.status === "unconfirmed" ? "orange" : "green",
            operationId: result.operationId,
          });
      } else {
        const result = await recoverGenerationNotificationForOwner(
          session.owner,
          { eventId: selected.id, candidateTs: candidate },
          controller.signal,
        );
        if (current())
          setMessage({
            text:
              recoveryMessage(result) +
              (result.retryAfterMs ? ` 대기 시간: ${Math.ceil(result.retryAfterMs / 1000)}초` : ""),
            color: result.status === "unconfirmed" ? "orange" : "green",
            operationId: result.operationId,
          });
      }
    } catch (error) {
      if (current())
        setMessage({
          text:
            error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0)
              ? "현재 관리자 권한을 확인할 수 없습니다. 다시 로그인해 확인해 주세요."
              : legacySelection
                ? "기존 발송 기록 대조 결과를 확인하지 못했습니다. 목록을 새로 조회해 현재 연결을 확인해 주세요."
                : "복구 응답을 확인하지 못했습니다. 목록을 새로 조회하고 원래 후보를 명시적으로 다시 확인해 주세요.",
          color: "red",
        });
    } finally {
      if (current()) {
        setNeedsRefresh(true);
        clearSelection();
        setBusy(false);
      }
      active.current = null;
    }
  }
  return (
    <TitleContainer title="생성 알림 확인">
      <Stack>
        <Text>
          근무표 생성 알림의 발송 상태입니다. 근무표 결과와 사용량은 해당 업무 화면에서 확인해
          주세요.
        </Text>
        <Text size="sm" c="dimmed">
          조회와 후보 확인은 감사 기록에 남습니다. 후보 확인은 원래 발송 확인만 기록하며 메시지를
          새로 보내지 않습니다.
        </Text>
        <Group align="end">
          <Select
            label="알림 상태"
            value={status}
            data={Object.entries(notificationStates).map(([value, label]) => ({ value, label }))}
            allowDeselect={false}
            disabled={busy || query.isFetching}
            onChange={(value) => {
              if (value && value in notificationStates) {
                setStatus(value as GenerationNotificationStatus);
                setCursors([undefined]);
                setNeedsRefresh(false);
                clearSelection();
                setMessage(null);
              }
            }}
          />
          <Button
            variant="light"
            loading={query.isFetching}
            disabled={busy}
            onClick={() => void refresh()}
          >
            목록 새로 조회
          </Button>
          {query.data && ready && (
            <Badge>{query.data.environment === "development" ? "개발" : "운영"} 환경</Badge>
          )}
        </Group>
        {message && (
          <Alert color={message.color} role="status">
            <Text>{message.text}</Text>
            {message.operationId && (
              <Text size="xs" style={{ overflowWrap: "anywhere" }}>
                확인 기록: {message.operationId}
              </Text>
            )}
          </Alert>
        )}
        {busy && (
          <Text role="status">
            {legacySelection
              ? "저장된 발송 기록을 대조하고 있습니다."
              : "후보 메시지를 확인하고 있습니다."}{" "}
            화면을 떠나거나 응답이 끊기면 목록에서 결과를 다시 확인해 주세요.
          </Text>
        )}
        {query.isFetching && <Text role="status">알림 목록을 확인하고 있습니다.</Text>}
        {query.isError && (
          <Alert color="red">
            알림 목록을 확인하지 못했습니다. 권한과 운영 설정을 확인한 뒤 다시 조회해 주세요.
          </Alert>
        )}
        {needsRefresh && (
          <Alert color="orange">현재 상태를 확인하려면 목록을 새로 조회해 주세요.</Alert>
        )}
        {ready && query.data?.items.length === 0 && (
          <Text>이 상태에 해당하는 알림이 없습니다.</Text>
        )}
        {ready &&
          query.data?.items.map((row) => (
            <Card withBorder key={row.id}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={700}>
                    {notificationApps[row.app]} · {notificationKinds[row.kind] ?? "유형 확인 필요"}
                  </Text>
                  <Badge>{notificationStates[row.status]}</Badge>
                </Group>
                <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                  알림 번호: {row.id}
                </Text>
                <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                  업무 번호: {row.sourceId ?? row.businessRequestId ?? "기록 없음"}
                </Text>
                <Text size="sm">
                  접수: {notificationTime(row.acceptedAt)} · 발송 시도: {row.attemptCount}회
                </Text>
                <Text size="sm">{notificationReason(row)}</Text>
                {row.lastError && (
                  <Text size="sm">최근 사유: {notificationError(row.lastError.code)}</Text>
                )}
                {row.latestAttempt && (
                  <Text size="xs" c="dimmed">
                    최근 시도: {notificationTime(row.latestAttempt.createdAt)} · 발송 확인:{" "}
                    {notificationTime(row.latestAttempt.acknowledgedAt)}
                  </Text>
                )}
                {row.status === "RETRY" && (
                  <Text size="sm">
                    다음 처리 예정: {notificationTime(row.nextAttemptAt)} (한국시간)
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {row.role === "PARENT" ? "요청 메시지" : "결과 답글"} · 부모 발송 확인:{" "}
                  {row.parentConfirmed ? "있음" : "없음"}
                </Text>
                {row.candidateLookupAvailable && (
                  <Button
                    variant="light"
                    onClick={() => {
                      setSelected(row);
                      setLegacySelection(false);
                      setCandidate("");
                      setConfirmed(false);
                      setMessage(null);
                    }}
                  >
                    이 알림의 후보 확인
                  </Button>
                )}
                {legacyReceiptAvailable(row) && (
                  <Button
                    variant="light"
                    onClick={() => {
                      setSelected(row);
                      setLegacySelection(true);
                      setCandidate("");
                      setConfirmed(false);
                      setMessage(null);
                    }}
                  >
                    저장된 기존 발송 기록 확인
                  </Button>
                )}
              </Stack>
            </Card>
          ))}
        {ready && selected && (
          <Card withBorder>
            <Stack>
              <Text fw={700}>{legacySelection ? "저장된 발송 기록 확인" : "후보 메시지 확인"}</Text>
              <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                선택한 알림: {selected.id}
              </Text>
              <Text size="sm">
                {legacySelection
                  ? "원래 업무에 저장된 채널과 발송 확인 기록만 대조해 연결합니다. 기록이 없거나 일치하지 않으면 연결을 보류합니다."
                  : "원래 채널의 정확한 후보 메시지를 조회합니다. 내용·작성자·시도가 모두 일치할 때 발송 확인을 기록합니다."}
              </Text>
              {!legacySelection && (
                <TextInput
                  ref={candidateInput}
                  label="후보 메시지 시각 (ts)"
                  description="예: 1792152000.000005. 메시지의 정확한 시각 값을 입력해 주세요."
                  value={candidate}
                  maxLength={23}
                  onChange={(event) => {
                    setCandidate(event.currentTarget.value);
                    setConfirmed(false);
                  }}
                />
              )}
              <Checkbox
                ref={confirmationInput}
                label={
                  legacySelection
                    ? "원래 업무에 저장된 발송 기록을 대조하겠습니다."
                    : "선택한 알림의 후보 메시지임을 확인했습니다."
                }
                checked={confirmed}
                onChange={(event) => setConfirmed(event.currentTarget.checked)}
              />
              <Group>
                <Button
                  disabled={
                    !confirmed || (!legacySelection && !/^\d{10,16}\.\d{6}$/.test(candidate))
                  }
                  onClick={() => void recover()}
                >
                  {legacySelection ? "저장된 기록 대조 및 연결" : "후보 조회 및 발송 확인"}
                </Button>
                <Button variant="subtle" onClick={clearSelection}>
                  취소
                </Button>
              </Group>
            </Stack>
          </Card>
        )}
        {ready && (
          <Group>
            <Button
              variant="light"
              disabled={cursors.length === 1}
              onClick={() => {
                setCursors((v) => v.slice(0, -1));
                clearSelection();
                setMessage(null);
              }}
            >
              이전 목록
            </Button>
            <Text size="sm">{cursors.length}쪽 · 최대 20건</Text>
            <Button
              variant="light"
              disabled={!query.data?.nextCursor}
              onClick={() => {
                if (query.data?.nextCursor)
                  setCursors((v) => [...v, query.data?.nextCursor ?? undefined]);
                clearSelection();
                setMessage(null);
              }}
            >
              다음 목록
            </Button>
          </Group>
        )}
      </Stack>
    </TitleContainer>
  );
}
